import { DragDropModule } from '@angular/cdk/drag-drop';
import { PlatformModule } from '@angular/cdk/platform';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AtaraxiaAngularReactiveModule } from '@epicuro-next/ataraxia/angular-reactive';
import { AsideMenuModule } from '@epicuro-next/ataraxia/aside-menu';
import { BadgeComponent } from '@epicuro-next/ataraxia/badge';
import { ButtonsModule } from '@epicuro-next/ataraxia/buttons';
import { ClickOutsideModule } from '@epicuro-next/ataraxia/click-outside';
import { ClickableAmountInputModule } from '@epicuro-next/ataraxia/clickable-amount-input';
import { FormattedLocaleDatePipe } from '@epicuro-next/ataraxia/date';
import { DiscountLabelModule } from '@epicuro-next/ataraxia/discount-label';
import { DomSanitizerModule } from '@epicuro-next/ataraxia/dom-sanitizer';
import { DropdownModule } from '@epicuro-next/ataraxia/dropdown';
import { EmptyPlaceholderModule } from '@epicuro-next/ataraxia/empty-placeholder';
import { AtaraxiaGridBuilderModule } from '@epicuro-next/ataraxia/grid-builder';
import { AtaraxiaGridNavigationModule } from '@epicuro-next/ataraxia/grid-navigation';
import { AtaraxiaHorizontalScrollArrowsModule } from '@epicuro-next/ataraxia/horizontal-scroll-arrows';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { ImageModule } from '@epicuro-next/ataraxia/image';
import { InputModule } from '@epicuro-next/ataraxia/input';
import { LoaderOverlayModule } from '@epicuro-next/ataraxia/loader-overlay';
import { PanelsModule } from '@epicuro-next/ataraxia/panels';
import { PopoverModule } from '@epicuro-next/ataraxia/popover';
import { ProductImageModule } from '@epicuro-next/ataraxia/product-image';
import { SkeletonBasicModule } from '@epicuro-next/ataraxia/skeleton-basic';
import { SwitchesModule } from '@epicuro-next/ataraxia/switches';
import { TooltipDirective } from '@epicuro-next/ataraxia/tooltip';
import { FeaturesAdvancedMdoGridModule } from '@epicuro-next/features/advanced-mdo-grid';
import {
  ConfigurationModule,
  CONFIG_STRIP_FEATURE,
} from '@epicuro-next/platform/configuration';
import { ProductListStoreModule } from '@epicuro-next/state/product-list-state';
import { LetDirective, PushPipe } from '@ngrx/component';

import { DeliveryPeriodModule } from '../../../route/src/lib/components/delivery-period/delivery-period.component.module';
// tslint:disable-next-line:nx-enforce-module-boundaries
// tslint:disable-next-line:nx-enforce-module-boundaries
import { InventoryOverviewModule } from '../../../route/src/lib/containers/inventory-overview/inventory-overview.component.module';

import { AdvancedPrePackComponent } from './components/advanced-pre-pack/advanced-pre-pack.component';
import { AdvancedSizeGridComponent } from './components/advanced-size-grid/advanced-size-grid.component';
import { CopyQuantities } from './components/copy-quantities/copy-quantities.component';
import { GridCellInboundComponent } from './components/grid-cell-inbound/grid-cell-inbound.component';
import { GridCellComponent } from './components/grid-cell/grid-cell.component';
import { GridHeaderComponent } from './components/grid-header/grid-header.component';
import { InboundDetailsComponent } from './components/inbound-details/inbound-details.component';
import { NeverOutOfStockIconComponent } from './components/never-out-of-stock-icon/never-out-of-stock-icon.component';
import { PrePackDetailsComponent } from './components/pre-pack-details/pre-pack-details.component';
import { PrePackHeaderComponent } from './components/pre-pack-header/pre-pack-header.component';
import { PrePackComponent } from './components/pre-pack/pre-pack.component';
import { ProductTitleComponent } from './components/product-title/product-title.component';
import { ScaleSelectorComponent } from './components/scale-selector/scale-selector.component';
import { PreviewSizeGridComponent } from './components/size-grid/preview-grid/preview-size-grid.component';
import { SizeGridComponent } from './components/size-grid/two-dimensional/size-grid.component';
import { VerticalSizeGridComponent } from './components/size-grid/vertical-size-grid/vertical-size-grid.component';
import { SizeRunsQuantitiesComponent } from './components/size-runs-quantities/size-runs-quantities.component';
import { DeliveryPeriodSkeletonComponent } from './components/skeletons/delivery-period-skeleton/delivery-period-skeleton.component';
import { GridHeaderSkeletonComponent } from './components/skeletons/grid-header-skeleton/grid-header-skeleton.component';
import { GridSkeletonComponent } from './components/skeletons/grid-skeleton/grid-skeleton.component';
import { ToggleDisplayModeComponent } from './components/toggle-display-mode/toggle-display-mode.component';
import { MaterialsGridComponent } from './containers/materials-grid/materials-grid.component';
import { PrePackConfiguratorComponent } from './containers/pre-pack-configurator/pre-pack-configurator.component';
import { PreviewProductSizesComponentV2 } from './containers/preview-product-sizes-v2/preview-product-sizes-v2.component';
import { PreviewProductSizesComponent } from './containers/preview-product-sizes/preview-product-sizes.component';

// tslint:disable-next-line:nx-enforce-module-boundaries

const PUBLIC_COMPONENTS = [
  ProductTitleComponent,
  DeliveryPeriodSkeletonComponent,
  GridHeaderSkeletonComponent,
  MaterialsGridComponent,
  NeverOutOfStockIconComponent,
  PreviewProductSizesComponent,
  PreviewProductSizesComponentV2,
  PrePackConfiguratorComponent,
  GridHeaderComponent,
  InboundDetailsComponent,
  GridSkeletonComponent,
  AdvancedPrePackComponent,
  PrePackHeaderComponent,
  PrePackDetailsComponent,
];

@NgModule({
  imports: [
    BadgeComponent,
    ClickableAmountInputModule,
    CommonModule,
    DiscountLabelModule,
    DomSanitizerModule,
    IconsModule,
    ImageModule,
    InputModule,
    DropdownModule,
    FormattedLocaleDatePipe,
    PanelsModule,
    PopoverModule,
    ProductListStoreModule,
    ReactiveFormsModule,
    LoaderOverlayModule,
    IconsModule,
    TooltipDirective,
    LetDirective,
    PushPipe,
    ClickOutsideModule,
    InventoryOverviewModule,
    DeliveryPeriodModule,
    ConfigurationModule.forFeature(CONFIG_STRIP_FEATURE),
    AsideMenuModule,
    RouterModule,
    SkeletonBasicModule,
    EmptyPlaceholderModule,
    AtaraxiaAngularReactiveModule,
    SwitchesModule,
    AtaraxiaGridNavigationModule,
    AtaraxiaHorizontalScrollArrowsModule,
    FeaturesAdvancedMdoGridModule,
    AtaraxiaGridBuilderModule,
    DragDropModule,
    ProductImageModule,
    PlatformModule,
    ButtonsModule,
  ],
  declarations: [
    PreviewSizeGridComponent,
    ScaleSelectorComponent,
    SizeGridComponent,
    VerticalSizeGridComponent,
    GridCellComponent,
    CopyQuantities,
    GridCellInboundComponent,
    ToggleDisplayModeComponent,
    PrePackComponent,
    ...PUBLIC_COMPONENTS,
    AdvancedSizeGridComponent,
    SizeRunsQuantitiesComponent,
  ],
  exports: PUBLIC_COMPONENTS,
})
export class ConfigStripComponentModule {}
